import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChildren, QueryList, AfterViewInit } from '@angular/core';
import { ShipmentOrderSpec } from '@core/models/job-file/shipment-order-spec.model';
import { SpecGroup } from '@core/models/job-file/panel-specs-response.model';
import { JobPlanOrderListItemsComponent } from '../job-plan-order-list-items/job-plan-order-list-items.component';
import { DialogHandlerService } from '@core/services/dialog-handler.service';
import { OrderFiltersComponent, OrderFilters } from '../order-filters/order-filters.component';

/** Server-side query the panel derives from the search/sort/group/filter controls. */
export interface PanelQuery {
  searchText: string;
  sortBy: 'totalVolume' | 'totalWeight' | 'consigneeName';
  sortOrder: 'ascending' | 'descending';
  groupBy: 'none' | 'consigneeName' | 'dropOffLocationName';
  otherFilters: OrderFilters | null;
}

@Component({
  selector: 'hmt-job-plan-order-display',
  templateUrl: './job-plan-order-display.component.html',
  styleUrls: ['./job-plan-order-display.component.scss'],
  standalone: false
})
export class JobPlanOrderDisplayComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() orders: ShipmentOrderSpec[] = [];
  @Input() selectable = false;
  @Input() deletable = true;
  @Input() showHeader = false;
  @Input() showSearch = false;
  @Input() hideCheckboxes = false;
  @Input() showMoveDropdown = false;
  @Input() containers: any[] = [];
  @Input() selectedLoadId: string = '';
  @Input() showAddButton = false;
  @Input() jobType?: string;
  // Server-driven paging: `orders` is the current page (ungrouped); when `grouped`, `serverGroups` holds the page of groups.
  @Input() grouped = false;
  @Input() serverGroups: SpecGroup[] = [];
  // Total across all pages (server-provided), used for select-all count.
  @Input() totalMatching = 0;
  @Output() removeShipmentOrder = new EventEmitter<string>();
  @Output() selectOrder = new EventEmitter<{ selected: boolean; order: ShipmentOrderSpec }>();
  @Output() loadSelected = new EventEmitter<string>();
  @Output() loadMoveClicked = new EventEmitter<string>();
  @Output() addButtonClicked = new EventEmitter<void>();
  // Emitted when a search/sort/group/other-filter control changes → parent refetches from the server.
  @Output() queryChange = new EventEmitter<PanelQuery>();

  @ViewChildren(JobPlanOrderListItemsComponent) orderListItemsComponents!: QueryList<JobPlanOrderListItemsComponent>;

  expandedOrderIds: Set<string> = new Set();
  selectedOrderIds: Set<string> = new Set();
  partiallySelectedOrderIds: Set<string> = new Set();
  searchText = '';
  filteredOrders: ShipmentOrderSpec[] = [];
  orderComponentsMap: Map<string, JobPlanOrderListItemsComponent> = new Map();
  // Map to track selected item IDs per order (orderId -> Set of itemRefIds)
  selectedItemIdsMap: Map<string, Set<string>> = new Map();
  // Snapshot of full order objects for every individually-selected order — persists across pages so
  // move can build its payload without holding the whole list (bounded by what the user picks).
  selectedSnapshot: Map<string, ShipmentOrderSpec> = new Map();
  // Select-all across ALL pages: dropped in-memory snapshot, resolved server-side at move time.
  isSelectAll = false;
  // Orders unchecked after clicking select-all (so "select all then uncheck a few" still works).
  excludeOrderIds: Set<string> = new Set();

  // Sorting properties
  sortBy: 'totalVolume' | 'totalWeight' | 'consigneeName' = 'totalVolume';
  sortOrder: 'ascending' | 'descending' = 'ascending';

  // Grouping properties
  groupBy: 'none' | 'consigneeName' | 'dropOffLocationName' = 'none';
  groupedOrders: Array<{ groupKey: string; orders: ShipmentOrderSpec[] }> = [];
  expandedGroupKeys: Set<string> = new Set();

  // Other filters
  otherFilters: OrderFilters | null = null;

  constructor(private dialogHandlerService: DialogHandlerService) {}

  ngOnInit(): void {
    this.syncFromInputs();
  }

  ngOnChanges(): void {
    // Render the server-provided page. Selection state persists across pages via selectedSnapshot,
    // so we intentionally do NOT prune it against the current page here.
    this.syncFromInputs();
  }

  /** Render the current server page directly (no client-side filter/sort/group). */
  private syncFromInputs(): void {
    this.filteredOrders = [...(this.orders || [])];
    this.groupedOrders = this.grouped
      ? (this.serverGroups || []).map(g => ({ groupKey: g.groupKey, orders: g.orders }))
      : [];
    setTimeout(() => this.updateOrderComponentsMap());
  }

  /** Builds the current control state into a query for the parent to fetch server-side. */
  private emitQuery(): void {
    this.queryChange.emit({
      searchText: this.searchText,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      groupBy: this.groupBy,
      otherFilters: this.otherFilters
    });
  }

  ngAfterViewInit(): void {
    // Map order IDs to their child components after view initialization
    this.orderListItemsComponents.changes.subscribe(() => {
      setTimeout(() => this.updateOrderComponentsMap());
    });
    setTimeout(() => this.updateOrderComponentsMap());
  }

  private updateOrderComponentsMap(): void {
    if (!this.orderListItemsComponents) {
      return;
    }
    
    const components = this.orderListItemsComponents.toArray();
    
    // Get all orders in display order (grouped or ungrouped)
    const ordersInDisplayOrder = this.groupBy !== 'none' && this.groupedOrders.length > 0
      ? this.groupedOrders.flatMap(group => group.orders)
      : this.filteredOrders;
    
    // Match components with expanded orders
    // Components are created in the same order as expanded orders appear in display
    let componentIndex = 0;
    ordersInDisplayOrder.forEach((order) => {
      if (this.isExpanded(order.orderId) && order.orderId) {
        if (componentIndex < components.length) {
          const component = components[componentIndex];
          this.orderComponentsMap.set(order.orderId, component);
          componentIndex++;
        }
      }
    });
  }

  openOtherFilters(event?: Event): void {
    // Calculate position below the button
    let position: { top?: string; left?: string; right?: string } = {};
    
    if (event && event.target) {
      const buttonElement = event.target as HTMLElement;
      const rect = buttonElement.getBoundingClientRect();
      const dialogWidth = 436; // Dialog width in pixels
      const spacing = 8; // Spacing between button and dialog
      
      // Position dialog below the button, aligned to left edge
      const top = rect.bottom + spacing;
      let left = rect.left;
      
      // Adjust if dialog would go off right edge
      if (left + dialogWidth > window.innerWidth) {
        left = window.innerWidth - dialogWidth - 20; // 20px margin from right edge
      }
      
      // Adjust if dialog would go off left edge
      if (left < 20) {
        left = 20; // 20px margin from left edge
      }
      
      position = {
        top: `${top}px`,
        left: `${left}px`
      };
    }

    const dialogRef = this.dialogHandlerService.openDialog(
      OrderFiltersComponent,
      {
        orders: this.orders,
        currentFilters: this.otherFilters || undefined
      },
      {
        width: '436px',
        height: 'auto',
        maxHeight: '90vh',
        position: position,
        panelClass: 'order-filters-dialog',
        hasBackdrop: true,
        backdropClass: 'order-filters-backdrop'
      }
    );

    dialogRef.afterClosed().subscribe((filters: OrderFilters | null | undefined) => {
      if (filters !== undefined) {
        // If filters is null, user clicked Reset All - clear all filters
        // If filters is an object, user applied filters
        this.otherFilters = filters;
        this.emitQuery();
      }
      // If filters is undefined, user clicked outside or ESC - don't change anything
    });
  }

  onSearchChange(): void {
    this.emitQuery();
  }

  onSortByChange(): void {
    this.emitQuery();
  }

  onSortOrderChange(): void {
    this.emitQuery();
  }

  onGroupByChange(): void {
    // Reset expanded groups when grouping changes
    this.expandedGroupKeys.clear();
    this.emitQuery();
  }

  toggleGroupExpand(groupKey: string): void {
    if (this.expandedGroupKeys.has(groupKey)) {
      this.expandedGroupKeys.delete(groupKey);
    } else {
      this.expandedGroupKeys.add(groupKey);
    }
  }

  isGroupExpanded(groupKey: string): boolean {
    return this.expandedGroupKeys.has(groupKey);
  }

  /** First letter for consignee group avatar (Figma grouped header). */
  getGroupInitial(groupKey: string): string {
    const s = (groupKey || '').trim();
    return s.length > 0 ? s.charAt(0).toUpperCase() : '?';
  }

  private applySorting(orders: ShipmentOrderSpec[]): ShipmentOrderSpec[] {
    if (!orders || orders.length === 0) {
      return [];
    }

    return [...orders].sort((a, b) => {
      let comparison = 0;

      switch (this.sortBy) {
        case 'totalVolume':
          comparison = (a.totalCBM || 0) - (b.totalCBM || 0);
          break;
        case 'totalWeight':
          comparison = (a.totalWeight || 0) - (b.totalWeight || 0);
          break;
        case 'consigneeName':
          const nameA = (a.consigneeName || '').toLowerCase();
          const nameB = (b.consigneeName || '').toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;
      }

      return this.sortOrder === 'ascending' ? comparison : -comparison;
    });
  }

  private applyGrouping(orders: ShipmentOrderSpec[]): Array<{ groupKey: string; orders: ShipmentOrderSpec[] }> {
    if (!orders || orders.length === 0 || this.groupBy === 'none') {
      return [];
    }

    const groupsMap = new Map<string, ShipmentOrderSpec[]>();

    orders.forEach(order => {
      let groupKey = '';
      
      switch (this.groupBy) {
        case 'consigneeName':
          groupKey = order.consigneeName || 'Unknown Consignee';
          break;
        case 'dropOffLocationName':
          groupKey = order.dropOffLocationName || 'Unknown Location';
          break;
      }

      if (!groupsMap.has(groupKey)) {
        groupsMap.set(groupKey, []);
      }
      groupsMap.get(groupKey)!.push(order);
    });

    // Convert map to array and sort groups by key
    const groups = Array.from(groupsMap.entries()).map(([groupKey, orders]) => ({
      groupKey,
      orders
    }));

    // Sort groups alphabetically by group key
    groups.sort((a, b) => a.groupKey.localeCompare(b.groupKey));

    return groups;
  }

  toggleExpand(orderId: string): void {
    if (this.expandedOrderIds.has(orderId)) {
      this.expandedOrderIds.delete(orderId);
      // Remove from map when collapsed
      this.orderComponentsMap.delete(orderId);
    } else {
      this.expandedOrderIds.add(orderId);
      // Update map when expanded - wait for component to render
      setTimeout(() => {
        this.updateOrderComponentsMap();
        // If order was already selected, select all its SKUs
        if (this.isOrderSelected(orderId)) {
          const component = this.orderComponentsMap.get(orderId);
          if (component) {
            component.selectAllItems();
          }
        }
      }, 0);
    }
  }

  isExpanded(orderId: string): boolean {
    return this.expandedOrderIds.has(orderId);
  }

  onDeleteOrder(order: ShipmentOrderSpec, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.removeShipmentOrder.emit(order.id ?? order.orderId);
  }

  onSelectOrder(order: ShipmentOrderSpec, event: any): void {
    const orderId = order.orderId;
    if (event.checked) {
      // Snapshot the full order so it survives paging away (bounded to selected orders).
      this.selectedSnapshot.set(orderId, JSON.parse(JSON.stringify(order)));
      // If in select-all mode, checking a previously-excluded row re-includes it.
      this.excludeOrderIds.delete(orderId);
      this.selectedOrderIds.add(orderId);
      this.partiallySelectedOrderIds.delete(orderId);
      // Automatically select all SKUs in this order
      const component = this.orderComponentsMap.get(orderId);
      if (component) {
        component.selectAllItems();
        // Update selected items map with all items
        const allItemIds = new Set<string>();
        order.shipmentOrders?.forEach(item => {
          if (item.itemRefId) {
            allItemIds.add(item.itemRefId);
          }
        });
        this.selectedItemIdsMap.set(orderId, allItemIds);
      } else {
        // Order not expanded, but still track all items as selected
        const allItemIds = new Set<string>();
        order.shipmentOrders?.forEach(item => {
          if (item.itemRefId) {
            allItemIds.add(item.itemRefId);
          }
        });
        this.selectedItemIdsMap.set(orderId, allItemIds);
      }
    } else {
      this.selectedOrderIds.delete(orderId);
      this.partiallySelectedOrderIds.delete(orderId);
      this.selectedItemIdsMap.delete(orderId);
      this.selectedSnapshot.delete(orderId);
      // In select-all mode, unchecking a row records it as an exclusion (backend skips it on move).
      if (this.isSelectAll) {
        this.excludeOrderIds.add(orderId);
      }
      // Deselect all SKUs in this order
      const component = this.orderComponentsMap.get(orderId);
      if (component) {
        component.deselectAllItems();
      }
      // Close expanded order-details when order is unselected
      if (this.expandedOrderIds.has(orderId)) {
        this.expandedOrderIds.delete(orderId);
        this.orderComponentsMap.delete(orderId);
      }
    }
    this.selectOrder.emit({ selected: event.checked, order });
  }

  onSkuItemSelected(orderId: string, event: { selected: boolean; order: any }): void {
    // Track individual item selection
    if (!this.selectedItemIdsMap.has(orderId)) {
      this.selectedItemIdsMap.set(orderId, new Set<string>());
    }
    
    const selectedItemIds = this.selectedItemIdsMap.get(orderId)!;
    
    if (event.selected) {
      if (event.order.itemRefId) {
        selectedItemIds.add(event.order.itemRefId);
      }
    } else {
      if (event.order.itemRefId) {
        selectedItemIds.delete(event.order.itemRefId);
      }
    }
    
    // Update map
    if (selectedItemIds.size === 0) {
      this.selectedItemIdsMap.delete(orderId);
    } else {
      this.selectedItemIdsMap.set(orderId, selectedItemIds);
    }
  }

  onSkuSelectionChanged(orderId: string, selectedCount: number, totalCount: number): void {
    // Update selected items map from component
    const component = this.orderComponentsMap.get(orderId);
    if (component) {
      const selectedItemIds = new Set<string>();
      component.selectedOrders.forEach(item => {
        if (item.itemRefId) {
          selectedItemIds.add(item.itemRefId);
        }
      });
      if (selectedItemIds.size > 0) {
        this.selectedItemIdsMap.set(orderId, selectedItemIds);
      } else {
        this.selectedItemIdsMap.delete(orderId);
      }
    }

    // Defer state changes to next change detection cycle to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      const order = this.filteredOrders.find(o => o.orderId === orderId);
      if (!order) {
        return;
      }

      if (selectedCount === 0) {
        if (this.selectedOrderIds.has(orderId)) {
          return;
        }
        // No SKUs selected - deselect order
        this.selectedOrderIds.delete(orderId);
        this.partiallySelectedOrderIds.delete(orderId);
        this.selectedItemIdsMap.delete(orderId);
        this.selectedSnapshot.delete(orderId);
        this.selectOrder.emit({ selected: false, order });
      } else if (selectedCount === totalCount) {
        // All SKUs selected - fully select order
        this.selectedSnapshot.set(orderId, JSON.parse(JSON.stringify(order)));
        this.excludeOrderIds.delete(orderId);
        this.selectedOrderIds.add(orderId);
        this.partiallySelectedOrderIds.delete(orderId);
        this.selectOrder.emit({ selected: true, order });
      } else {
        // Some SKUs selected - partially select order
        this.selectedSnapshot.set(orderId, JSON.parse(JSON.stringify(order)));
        this.excludeOrderIds.delete(orderId);
        this.selectedOrderIds.delete(orderId);
        this.partiallySelectedOrderIds.add(orderId);
      }
    }, 0);
  }

  isOrderSelected(orderId: string): boolean {
    // In select-all mode every row is checked except those the user explicitly excluded.
    if (this.isSelectAll) {
      return !this.excludeOrderIds.has(orderId);
    }
    return this.selectedOrderIds.has(orderId);
  }

  isOrderPartiallySelected(orderId: string): boolean {
    return this.partiallySelectedOrderIds.has(orderId);
  }

  calculateRemainingTime(deliveryEndDateTime: Date): string {
    try {
      const deliveryTime = new Date(deliveryEndDateTime).getTime();
      const currentTime = new Date().getTime();
      const seconds = Math.floor((deliveryTime - currentTime) / 1000);
      
      const minutes = Math.abs(seconds % 60);
      const days = Math.floor(seconds / (3600 * 24));
      const hours = Math.abs(Math.floor(((seconds / (3600 * 24)) % 1) * 24));
      
      return `${days}D ${hours}H ${minutes}M`;
    } catch (e) {
      return '';
    }
  }

  getActiveFiltersCount(): number {
    if (!this.otherFilters) {
      return 0;
    }

    let count = 0;
    const filters = this.otherFilters;

    // Count date filters
    if (filters.deliveryOnOrBeforeDate) {
      count++;
    }
    if (filters.deliveryOnOrAfterDate) {
      count++;
    }

    // Count weight filter (if range is narrowed from slider defaults)
    // Compare against slider min/max to determine if filter is active
    const weightSliderMin = filters.weightSliderMin ?? 20;
    const weightSliderMax = filters.weightSliderMax ?? 70;
    const hasWeightFilter = filters.weightMin !== weightSliderMin || filters.weightMax !== weightSliderMax;
    if (hasWeightFilter) {
      count++;
    }

    // Count capacity filter (if range is narrowed from slider defaults)
    // Compare against slider min/max to determine if filter is active
    const capacitySliderMin = filters.capacitySliderMin ?? 0;
    const capacitySliderMax = filters.capacitySliderMax ?? 100;
    const hasCapacityFilter = filters.capacityMin !== capacitySliderMin || filters.capacityMax !== capacitySliderMax;
    if (hasCapacityFilter) {
      count++;
    }

    return count;
  }

  /**
   * Get selected orders with their selected SKUs
   * Returns orders that are either fully selected or partially selected (with only selected SKUs)
   */
  getSelectedOrdersWithSelectedSkus(): ShipmentOrderSpec[] {
    const selectedOrders: ShipmentOrderSpec[] = [];

    // Build from the persisted selection snapshot so selections made on other pages are included.
    const allOrders = Array.from(this.selectedSnapshot.values());

    allOrders.forEach(order => {
      const orderId = order.orderId;
      const isFullySelected = this.selectedOrderIds.has(orderId);
      const isPartiallySelected = this.partiallySelectedOrderIds.has(orderId);

      if (isFullySelected) {
        // Fully selected → all SKUs. Quantities come from the snapshot, which is kept in sync with
        // move-mode quantity edits via onSkuQuantityChanged().
        selectedOrders.push(JSON.parse(JSON.stringify(order)));
      } else if (isPartiallySelected) {
        // Partially selected → only the selected SKUs, at their (possibly edited) snapshot quantities.
        const selectedItemIds = this.selectedItemIdsMap.get(orderId);
        if (selectedItemIds && selectedItemIds.size > 0 && order.shipmentOrders) {
          const selectedSkus = order.shipmentOrders
            .filter(item => item.itemRefId && selectedItemIds.has(item.itemRefId))
            .map(item => JSON.parse(JSON.stringify(item)));
          if (selectedSkus.length > 0) {
            const orderCopy = JSON.parse(JSON.stringify(order));
            orderCopy.shipmentOrders = selectedSkus;
            selectedOrders.push(orderCopy);
          }
        }
      }
    });

    return selectedOrders;
  }

  /** Persist a move-mode SKU quantity edit into the selection snapshot (source of truth for the move payload). */
  onSkuQuantityChanged(orderId: string, event: { order: { itemRefId?: string }; newQuantity: number }): void {
    const snapshot = this.selectedSnapshot.get(orderId);
    if (!snapshot || !snapshot.shipmentOrders || !event.order?.itemRefId) {
      return;
    }
    const sku = snapshot.shipmentOrders.find(s => s.itemRefId === event.order.itemRefId);
    if (sku) {
      sku.quantity = event.newQuantity;
    }
  }

  /**
   * Check if any orders or items are selected
   * Returns true if there are any fully selected orders or partially selected orders with selected items
   */
  hasSelectedOrdersOrItems(): boolean {
    // Select-all across pages counts as a selection (unless everything was excluded).
    if (this.isSelectAll && this.excludeOrderIds.size < this.totalMatching) {
      return true;
    }
    // Snapshot holds every individually-picked order across pages.
    if (this.selectedSnapshot.size > 0) {
      return true;
    }
    // Check if any orders are fully selected
    if (this.selectedOrderIds.size > 0) {
      return true;
    }

    // Check if any orders are partially selected with selected items
    if (this.partiallySelectedOrderIds.size > 0) {
      // Check if any partially selected order has selected items
      for (const orderId of this.partiallySelectedOrderIds) {
        const selectedItemIds = this.selectedItemIdsMap.get(orderId);
        if (selectedItemIds && selectedItemIds.size > 0) {
          return true;
        }
        
        // Also check component if order is expanded
        const component = this.orderComponentsMap.get(orderId);
        if (component && component.selectedOrders.length > 0) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Clear all selection state - useful after moving orders to containers
   */
  clearSelectionState(): void {
    this.selectedOrderIds.clear();
    this.partiallySelectedOrderIds.clear();
    this.selectedItemIdsMap.clear();
    this.selectedSnapshot.clear();
    this.isSelectAll = false;
    this.excludeOrderIds.clear();

    // Deselect all items in expanded components
    this.orderComponentsMap.forEach((component) => {
      component.deselectAllItems();
    });
  }

  /**
   * True when a search string or an "other filters" selection is narrowing the server query.
   * Keeps the sort / group / filter toolbar on screen for a filtered zero-result, so the user can
   * widen the criteria instead of being stuck on the "No orders found" message.
   */
  get hasActiveFilter(): boolean {
    return !!this.searchText || this.getActiveFiltersCount() > 0;
  }

  /**
   * Clears the search box and the "other filters" selection. Sort/group are left alone — they are
   * display preferences, not filters. Intentionally does NOT emit a query: the parent calls this
   * while it is already refetching with the cleared query.
   */
  resetFilters(): void {
    this.searchText = '';
    this.otherFilters = null;
  }

  onAddButtonClick(): void {
    this.addButtonClicked.emit();
  }

  onLoadMoveClick(loadId: string): void {
    this.loadMoveClicked.emit(loadId);
  }

  /**
   * Select-all is checked when every matching order (across all pages) is selected with nothing excluded.
   */
  isSelectAllChecked(): boolean {
    if (this.isSelectAll) {
      return this.excludeOrderIds.size === 0;
    }
    if (this.filteredOrders.length === 0) {
      return false;
    }
    return this.filteredOrders.every(order => this.selectedOrderIds.has(order.orderId));
  }

  isSelectAllIndeterminate(): boolean {
    if (this.isSelectAll) {
      return this.excludeOrderIds.size > 0;
    }
    const anySelected = this.selectedSnapshot.size > 0;
    const allPageSelected = this.filteredOrders.length > 0
      && this.filteredOrders.every(order => this.selectedOrderIds.has(order.orderId));
    return anySelected && !allPageSelected;
  }

  /**
   * Select-all toggles the across-all-pages mode. Per product decision the in-memory snapshot is
   * dropped and the move is resolved server-side (isSelectAll + excludeOrderIds).
   */
  onSelectAllChange(checked: boolean): void {
    // Reset any per-order selection either way.
    this.selectedOrderIds.clear();
    this.partiallySelectedOrderIds.clear();
    this.selectedItemIdsMap.clear();
    this.selectedSnapshot.clear();
    this.excludeOrderIds.clear();
    this.orderComponentsMap.forEach((component) => component.deselectAllItems());
    this.isSelectAll = checked;
  }

  /** Move context for the parent: either explicit snapshot orders, or the select-all directive. */
  getMoveContext(): { isSelectAll: boolean; orders: ShipmentOrderSpec[]; excludeOrderIds: string[] } {
    if (this.isSelectAll) {
      return { isSelectAll: true, orders: [], excludeOrderIds: Array.from(this.excludeOrderIds) };
    }
    return { isSelectAll: false, orders: this.getSelectedOrdersWithSelectedSkus(), excludeOrderIds: [] };
  }

  /** Count of selected orders for the footer text (select-all uses the server total minus exclusions). */
  getSelectedCount(): number {
    if (this.isSelectAll) {
      return Math.max(0, this.totalMatching - this.excludeOrderIds.size);
    }
    return this.selectedSnapshot.size;
  }
}

